import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import type {
  User,
  Group,
  Discussion,
  Post,
  Event,
  Notification,
  Badge,
  Challenge,
  LeaderboardEntry,
  ApiResponse,
  PaginatedResponse,
  CreateGroupRequest,
  UpdateGroupRequest,
  CreateDiscussionRequest,
  UpdateDiscussionRequest,
  CreatePostRequest,
  UpdatePostRequest,
  CreateEventRequest,
  UpdateEventRequest,
  LoginRequest,
  RegisterRequest,
  UpdateProfileRequest,
  GroupFilters,
  DiscussionFilters,
  EventFilters,
  PaginationParams,
  GroupMember,
  EventAttendee,
  UserBadge,
  ChallengeParticipant
} from '@/types'
import { brandConfig } from '@/config/brand'

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: brandConfig.api.baseURL,
  timeout: brandConfig.api.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token if available (access_token for Bheem Passport)
    const token = localStorage.getItem('access_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor with automatic token refresh
let isRefreshing = false
let failedQueue: any[] = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest: any = error.config

    // Don't intercept 401 errors from login or register endpoints
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                          originalRequest.url?.includes('/auth/register')

    // If error is 401 and we haven't retried yet (and not from auth endpoint)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token
          return apiClient(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = localStorage.getItem('refresh_token')

      if (!refreshToken) {
        // No refresh token, redirect to login
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'
        return Promise.reject(error)
      }

      try {
        // Call refresh token endpoint
        const response = await axios.post(`${brandConfig.api.baseURL}/api/v1/auth/refresh`, {
          refresh_token: refreshToken
        })

        const { access_token, refresh_token: new_refresh_token } = response.data

        // Update tokens
        localStorage.setItem('access_token', access_token)
        localStorage.setItem('refresh_token', new_refresh_token)

        // Update header
        apiClient.defaults.headers.common['Authorization'] = 'Bearer ' + access_token
        originalRequest.headers['Authorization'] = 'Bearer ' + access_token

        processQueue(null, access_token)
        isRefreshing = false

        // Retry original request
        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        isRefreshing = false

        // Refresh failed, redirect to login
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'

        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

// Helper function to handle API errors
const handleApiError = (error: any): never => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message || error.message
    throw new Error(message)
  }
  throw error
}

// Auth API
export const authApi = {
  login: async (data: LoginRequest): Promise<{ access_token: string; refresh_token: string; token_type: string; expires_in: number; user: any }> => {
    try {
      const response = await apiClient.post('/api/v1/auth/login', data)
      return response.data
    } catch (error) {
      throw error
    }
  },

  register: async (data: RegisterRequest): Promise<any> => {
    try {
      const response = await apiClient.post('/api/v1/auth/register', data)
      return response.data
    } catch (error) {
      throw error
    }
  },

  refreshToken: async (refreshToken: string): Promise<{ access_token: string; refresh_token: string; user?: any }> => {
    try {
      const response = await apiClient.post('/api/v1/auth/refresh', {
        refresh_token: refreshToken
      })
      return response.data
    } catch (error) {
      throw error
    }
  },

  getCurrentUser: async (): Promise<any> => {
    try {
      const response = await apiClient.get('/api/v1/auth/me')
      return response.data
    } catch (error) {
      throw error
    }
  },

  getCompanies: async (): Promise<any[]> => {
    try {
      const response = await apiClient.get('/api/v1/auth/companies')
      return response.data
    } catch (error) {
      throw error
    }
  },

  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/api/v1/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    }
  },

  // OAuth endpoints
  googleLogin: async (): Promise<ApiResponse<{ authorization_url: string }>> => {
    try {
      const response = await apiClient.get('/api/auth/oauth/google/login')
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  googleCallback: async (code: string, state: string): Promise<ApiResponse<{ user: User; token: string }>> => {
    try {
      const response = await apiClient.get('/api/auth/oauth/google/callback', {
        params: { code, state }
      })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  githubLogin: async (): Promise<ApiResponse<{ authorization_url: string }>> => {
    try {
      const response = await apiClient.get('/api/auth/oauth/github/login')
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  githubCallback: async (code: string, state: string): Promise<ApiResponse<{ user: User; token: string }>> => {
    try {
      const response = await apiClient.get('/api/auth/oauth/github/callback', {
        params: { code, state }
      })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },
}

// Users API
export const usersApi = {
  getProfile: async (userId: string): Promise<ApiResponse<User>> => {
    try {
      const response = await apiClient.get(`/api/users/${userId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  updateProfile: async (data: UpdateProfileRequest): Promise<ApiResponse<User>> => {
    try {
      const response = await apiClient.patch('/api/v1/users/me', data)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  uploadAvatar: async (file: File): Promise<ApiResponse<{ avatar_url: string }>> => {
    try {
      const formData = new FormData()
      formData.append('avatar', file)
      const response = await apiClient.post('/api/v1/users/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },
}

// Groups API
export const groupsApi = {
  getAll: async (params?: GroupFilters & PaginationParams): Promise<PaginatedResponse<Group>> => {
    try {
      const response = await apiClient.get('/api/v1/groups', { params })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  getById: async (groupId: string): Promise<ApiResponse<Group>> => {
    try {
      const response = await apiClient.get(`/api/groups/${groupId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  create: async (data: CreateGroupRequest): Promise<ApiResponse<Group>> => {
    try {
      const response = await apiClient.post('/api/v1/groups', data)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  update: async (groupId: string, data: UpdateGroupRequest): Promise<ApiResponse<Group>> => {
    try {
      const response = await apiClient.patch(`/api/groups/${groupId}`, data)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  delete: async (groupId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.delete(`/api/groups/${groupId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  getMembers: async (groupId: string, params?: PaginationParams): Promise<PaginatedResponse<GroupMember>> => {
    try {
      const response = await apiClient.get(`/api/groups/${groupId}/members`, { params })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  join: async (groupId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.post(`/api/groups/${groupId}/join`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  leave: async (groupId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.post(`/api/groups/${groupId}/leave`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },
}

// Discussions API
export const discussionsApi = {
  getAll: async (params?: DiscussionFilters & PaginationParams): Promise<PaginatedResponse<Discussion>> => {
    try {
      const response = await apiClient.get('/api/v1/discussions', { params })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  getById: async (discussionId: string): Promise<ApiResponse<Discussion>> => {
    try {
      const response = await apiClient.get(`/api/discussions/${discussionId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  create: async (data: CreateDiscussionRequest): Promise<ApiResponse<Discussion>> => {
    try {
      const response = await apiClient.post('/api/v1/discussions', data)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  update: async (discussionId: string, data: UpdateDiscussionRequest): Promise<ApiResponse<Discussion>> => {
    try {
      const response = await apiClient.patch(`/api/discussions/${discussionId}`, data)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  delete: async (discussionId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.delete(`/api/discussions/${discussionId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  vote: async (discussionId: string, voteType: 'up' | 'down'): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.post(`/api/discussions/${discussionId}/vote`, { vote_type: voteType })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  removeVote: async (discussionId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.delete(`/api/discussions/${discussionId}/vote`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },
}

// Posts API
export const postsApi = {
  getByDiscussion: async (discussionId: string, params?: PaginationParams): Promise<PaginatedResponse<Post>> => {
    try {
      const response = await apiClient.get(`/api/discussions/${discussionId}/posts`, { params })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  create: async (data: CreatePostRequest): Promise<ApiResponse<Post>> => {
    try {
      const response = await apiClient.post('/api/posts', data)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  update: async (postId: string, data: UpdatePostRequest): Promise<ApiResponse<Post>> => {
    try {
      const response = await apiClient.patch(`/api/posts/${postId}`, data)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  delete: async (postId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.delete(`/api/posts/${postId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  vote: async (postId: string, voteType: 'up' | 'down'): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.post(`/api/posts/${postId}/vote`, { vote_type: voteType })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  removeVote: async (postId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.delete(`/api/posts/${postId}/vote`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },
}

// Events API
export const eventsApi = {
  getAll: async (params?: EventFilters & PaginationParams): Promise<PaginatedResponse<Event>> => {
    try {
      const response = await apiClient.get('/api/v1/events', { params })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  getById: async (eventId: string): Promise<ApiResponse<Event>> => {
    try {
      const response = await apiClient.get(`/api/events/${eventId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  create: async (data: CreateEventRequest): Promise<ApiResponse<Event>> => {
    try {
      const response = await apiClient.post('/api/v1/events', data)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  update: async (eventId: string, data: UpdateEventRequest): Promise<ApiResponse<Event>> => {
    try {
      const response = await apiClient.patch(`/api/events/${eventId}`, data)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  delete: async (eventId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.delete(`/api/events/${eventId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  getAttendees: async (eventId: string, params?: PaginationParams): Promise<PaginatedResponse<EventAttendee>> => {
    try {
      const response = await apiClient.get(`/api/events/${eventId}/attendees`, { params })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  register: async (eventId: string, status: 'going' | 'maybe'): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.post(`/api/events/${eventId}/register`, { status })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  unregister: async (eventId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.delete(`/api/events/${eventId}/register`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },
}

// Notifications API
export const notificationsApi = {
  getAll: async (params?: PaginationParams): Promise<PaginatedResponse<Notification>> => {
    try {
      const response = await apiClient.get('/api/notifications', { params })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  markAsRead: async (notificationId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.patch(`/api/notifications/${notificationId}/read`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  markAllAsRead: async (): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.patch('/api/notifications/read-all')
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  delete: async (notificationId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.delete(`/api/notifications/${notificationId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },
}

// Gamification API
export const gamificationApi = {
  getLeaderboard: async (params?: { period?: 'all' | 'month' | 'week' } & PaginationParams): Promise<PaginatedResponse<LeaderboardEntry>> => {
    try {
      const response = await apiClient.get('/api/v1/gamification/leaderboard', { params })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  getBadges: async (): Promise<ApiResponse<Badge[]>> => {
    try {
      const response = await apiClient.get('/api/v1/gamification/badges')
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  getUserBadges: async (userId: string): Promise<ApiResponse<UserBadge[]>> => {
    try {
      const response = await apiClient.get(`/api/gamification/users/${userId}/badges`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  getChallenges: async (params?: { status?: 'upcoming' | 'active' | 'completed' }): Promise<ApiResponse<Challenge[]>> => {
    try {
      const response = await apiClient.get('/api/v1/gamification/challenges', { params })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  joinChallenge: async (challengeId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.post(`/api/gamification/challenges/${challengeId}/join`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  getChallengeProgress: async (challengeId: string): Promise<ApiResponse<ChallengeParticipant>> => {
    try {
      const response = await apiClient.get(`/api/gamification/challenges/${challengeId}/progress`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },
}

// ============================================================================
// CONTENT & CREATOR ECONOMY API (NEW)
// ============================================================================

// Content API
export const contentApi = {
  getFeed: async (params?: { page?: number; limit?: number; content_type?: string; category?: string }): Promise<PaginatedResponse<any>> => {
    try {
      const response = await apiClient.get('/api/v1/content/feed', { params })
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  getById: async (contentId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.get(`/api/v1/content/${contentId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  create: async (data: any): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.post('/api/v1/content/', data)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  update: async (contentId: string, data: any): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.patch(`/api/v1/content/${contentId}`, data)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  publish: async (contentId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.post(`/api/v1/content/${contentId}/publish`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  delete: async (contentId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.delete(`/api/v1/content/${contentId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },
}

// Follows API
export const followsApi = {
  follow: async (userId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.post(`/api/v1/content/follows/${userId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  unfollow: async (userId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.delete(`/api/v1/content/follows/${userId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  getFollowers: async (userId: string): Promise<ApiResponse<any[]>> => {
    try {
      const response = await apiClient.get(`/api/v1/content/follows/followers/${userId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  getFollowing: async (userId: string): Promise<ApiResponse<any[]>> => {
    try {
      const response = await apiClient.get(`/api/v1/content/follows/following/${userId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },
}

// Reactions API
export const reactionsApi = {
  add: async (data: { target_type: string; target_id: string; reaction_type: string }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.post('/api/v1/content/reactions', data)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  remove: async (targetType: string, targetId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.delete(`/api/v1/content/reactions/${targetType}/${targetId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },
}

// Comments API
export const commentsApi = {
  create: async (data: { target_type: string; target_id: string; comment_text: string; parent_comment_id?: string }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.post('/api/v1/content/comments', data)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },

  getByTarget: async (targetType: string, targetId: string): Promise<ApiResponse<any[]>> => {
    try {
      const response = await apiClient.get(`/api/v1/content/comments/${targetType}/${targetId}`)
      return response.data
    } catch (error) {
      return handleApiError(error)
    }
  },
}

export default apiClient
